const { PassThrough } = require('stream');
const FileType = require('file-type/browser');
const fs = require('fs');
const { saveAs } = require('file-saver');
const utils = require('../utils');
const TdfNavigator = require('../utils/tdf-navigator');

/**
 * A {@link https://nodejs.org/api/stream.html#stream_class_stream_readable|Readable} stream implementation that exposes convenience methods for writing
 * to various locations like files and strings.
 * <pre>
 // Run a decrypt, which returns a PlaintextStream.
 plaintextStream = await client.decrypt(decryptParams);

 // Write the plaintext to a local file.
 await stream.toFile("/tmp/plain.txt");
   </pre>
 * @see Client#encrypt
 * @hideconstructor
 */
class PlaintextStream extends PassThrough {
  constructor(byteLimit) {
    // see: https://nodejs.org/api/stream.html#stream_buffering
    super({ highWaterMark: byteLimit });
  }

  /**
   * Dump the stream content to a string. This will consume the stream.
   * @param {string} encoding - the charset encoding to use. Defaults to utf-8.
   * @return {string} - the plaintext in string form.
   */
  async toString(encoding) {
    return utils.streamToString(this, encoding);
  }

  /**
   * Dump the stream content to a buffer. This will consume the stream.
   * @return {Buffer} - the plaintext in Buffer form.
   */
  async toBuffer() {
    return utils.streamToBuffer(this);
  }

  /**
   * Dump the stream content to a local file. This will consume the stream.
   *
   * @param {string} filepath - the path of the local file to write plaintext to.
   * @param {string} encoding - the charset encoding to use. Defaults to utf-8.
   */
  async toFile(filepath, encoding) {
    if (utils.inBrowser()) {
      const data = await utils.streamToBuffer(this);
      const mimetype = await this._getMIMEType(data);
      const fileName =
        filepath || mimetype.mime === 'application/octet-stream' ? 'download.tdf' : '';

      this._saveFile(data, mimetype, fileName);
    } else {
      return new Promise((resolve, reject) => {
        const file = fs.createWriteStream(filepath, { encoding: encoding || 'utf-8', flag: 'w' });
        this.pipe(file);
        file.on('finish', () => {
          resolve();
        });
      });
    }
  }

  async _getMIMEType(fileBuffer) {
    const fileType = await FileType.fromBuffer(fileBuffer);
    // Check if its zip or tdf.
    if (fileType.mime === 'application/zip') {
      const zipHelper = new TdfNavigator({ type: 'buffer', location: fileBuffer });
      const centralDirectory = await zipHelper.getCentralDirectory();

      const isTdf =
        centralDirectory.find(({ fileName }) => fileName === '0.payload') &&
        centralDirectory.find(({ fileName }) => fileName === '0.manifest.json');

      if (isTdf) {
        return { mime: 'application/octet-stream' };
      }
    }
    return fileType;
  }

  _saveFile(filedata, { mime }, filename) {
    const blob = new Blob([filedata], { type: mime });
    saveAs(blob, filename);
  }

  getBufferedLength() {
    // For some reason, local and buildkite PassThrough have different properties. So we unify them here.
    const readableLength = this.readableLength || this._readableState.length;
    const writableLength = this.writableLength || this._writableState.length;
    return readableLength + writableLength;
  }

  async getMetadata() {
    return new Promise((resolve, reject) => {
      if (this.metadata) {
        resolve(this.metadata);
      } else {
        this.on('rewrap', (rewrapResponse) => {
          this.metadata = rewrapResponse;
          resolve(rewrapResponse);
        });
      }
    });
  }

  write(chunk, cb) {
    super.write(chunk, cb);
    // By default PassThrough simply delegates to WritableStream, which only checks the write buffer.
    // But we want to ensure the read buffer is being consumed, so take that into consideration as well.
    return this.getBufferedLength() < this.readableHighWaterMark;
  }
}

/**
 * A {@link https://nodejs.org/api/stream.html#stream_class_stream_readable|Readable} stream implementation that exposes convenience methods for writing
 * TDF ciphertext to various locations like files and strings.
 * <pre>
 // Run an encrypt, which returns a TDFCiphertextStream.
 ciphertextStream = await client.encrypt(encryptParams);

 // Write the TDF ciphertext to a local file.
 await stream.toFile("/tmp/plain.tdf");
   </pre>
 * @see Client#decrypt
 * @hideconstructor
 */
class TDFCiphertextStream extends PlaintextStream {
  constructor(byteLimit) {
    // see: https://nodejs.org/api/stream.html#stream_buffering
    super(byteLimit);
  }
  // TODO: Support reading in through this as well (unify with tdv-navigator)?

  /**
   * Dump the stream content to a string. This will consume the stream.
   * @return {string} - the TDF ciphertext in string form, encoded as binary.
   */
  async toString() {
    return super.toString('binary');
  }

  /**
   * Dump the stream content to a buffer. This will consume the stream.
   * @return {Buffer} - the TDF ciphertext in Buffer form.
   */
  async toBuffer() {
    return super.toBuffer();
  }

  /**
   * Dump the stream content to a local file. This will consume the stream.
   * @param {string} filepath - the path of the local file to write TDF ciphertext to.
   */
  async toFile(filepath) {
    return super.toFile(filepath, 'binary');
  }
}

module.exports = {
  PlaintextStream,
  TDFCiphertextStream,
};
